import { collection, onSnapshot, orderBy, query } from 'firebase/firestore'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { db } from '../services/firebase'
import { messagingApi } from '../services/messagingApi'

export const useMessagingStore = defineStore('messaging', () => {
  const conversations = ref([])
  const currentConversation = ref(null)
  const messages = ref([])
  const loading = ref(false)
  const error = ref(null)
  const securityBanner = ref(null)
  
  // Real-time unsubscribe functions
  let conversationsUnsubscribe = null
  let messagesUnsubscribe = null
  
  // Widget UI State
  const isWidgetOpen = ref(false)
  const showConversationList = ref(true)

  const toggleWidget = () => {
    isWidgetOpen.value = !isWidgetOpen.value
    if (isWidgetOpen.value && conversations.value.length === 0) {
      subscribeToConversations()
    }
  }

  const openChatWith = async (installerId) => {
    isWidgetOpen.value = true
    
    // Find existing conversation with this installer
    const existing = conversations.value.find(c => c.installer_id === installerId)
    
    if (existing) {
      currentConversation.value = existing
      showConversationList.value = false
      subscribeToMessages(existing.id)
    } else {
      // Setup UI for a new conversation state
      currentConversation.value = { id: 'new', installer_id: installerId, isNew: true }
      messages.value = []
      showConversationList.value = false
    }
  }

  const subscribeToConversations = () => {
    if (conversationsUnsubscribe) return
    
    // Only subscribe if we have a Firebase user for real-time Firestore access
    if (!userStore.firebaseUser) {
      console.log('[MessagingStore] Skipping Firestore subscription for local/unauthenticated session')
      return
    }

    loading.value = true
    const q = query(collection(db, 'conversations'), orderBy('lastMessageAt', 'desc'))
    
    conversationsUnsubscribe = onSnapshot(q, (snapshot) => {
      conversations.value = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      loading.value = false
    }, (err) => {
      console.error('[MessagingStore] Conversations sync failed:', err)
      error.value = 'Failed to sync conversations.'
      loading.value = false
    })
  }

  const subscribeToMessages = (conversationId) => {
    stopMessageSync()
    
    // Only subscribe if we have a Firebase user
    if (!userStore.firebaseUser) {
      console.log('[MessagingStore] Skipping messages subscription for local/unauthenticated session')
      return
    }

    const q = query(
      collection(db, 'conversations', conversationId, 'messages'), 
      orderBy('createdAt', 'asc')
    )
    
    messagesUnsubscribe = onSnapshot(q, (snapshot) => {
      messages.value = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    }, (err) => {
      console.error('[MessagingStore] Messages sync failed:', err)
      error.value = 'Failed to sync messages.'
    })
  }

  const stopMessageSync = () => {
    if (messagesUnsubscribe) {
      messagesUnsubscribe()
      messagesUnsubscribe = null
    }
  }

  const stopAllSync = () => {
    if (conversationsUnsubscribe) {
      conversationsUnsubscribe()
      conversationsUnsubscribe = null
    }
    stopMessageSync()
  }

  const fetchConversations = async () => {
    // Fallback if real-time not desired, but we prefer sync
    subscribeToConversations()
  }

  const fetchMessages = async (conversationId) => {
    subscribeToMessages(conversationId)
  }

  const sendMessage = async (conversationId, text, attachmentFile = null) => {
    try {
      let attachments = []
      if (attachmentFile) {
        const uploadRes = await messagingApi.uploadAttachment(attachmentFile)
        attachments = [uploadRes.data.data]
      }

      // PRD 8: Simple server-managed encryption envelope
      const encryptedBody = btoa(text)
      const res = await messagingApi.sendMessage(conversationId, { 
        encryptedBody,
        encryptionMetadata: { scheme: 'client_envelope_v1', encoding: 'base64' },
        attachments
      })
      // No need to re-fetch; Firestore sync will update the UI automatically
      return res.data.data
    } catch (err) {
      error.value = 'Failed to send message.'
      throw err
    }
  }

  const fetchSecurityBanner = async () => {
    try {
      const res = await messagingApi.getSecurityBanner()
      securityBanner.value = res.data.data
    } catch (err) {
      console.warn('Failed to fetch messaging security banner')
    }
  }

  // Polling is deprecated in favor of real-time Firestore
  const startPolling = (conversationId) => subscribeToMessages(conversationId)
  const stopPolling = () => stopMessageSync()

  const createConversation = async (payload) => {
    loading.value = true
    try {
      const res = await messagingApi.createConversation(payload)
      // Firestore will pick up the new conversation automatically via listener
      return res.data.data
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to start conversation.'
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    conversations,
    currentConversation,
    messages,
    loading,
    error,
    securityBanner,
    isWidgetOpen,
    showConversationList,
    toggleWidget,
    openChatWith,
    fetchConversations,
    fetchMessages,
    sendMessage,
    fetchSecurityBanner,
    startPolling,
    stopPolling,
    createConversation,
    stopAllSync
  }
})
